import { useEffect, useState } from 'react';
import { theme } from '../design-system';
import { Flex } from './shared';
import { keyframes } from '@emotion/react';
import { useTestContext } from '../../../context/testContext';
import { useBreakpoints } from '../../../helpers/useBreakpoints';

const blink = keyframes`
0% {
  opacity: 1
} 

50% {
  opacity: 0
}
100% {
  opacity: 1
}`;

const GraphLine = ({ horizontal }) => {
  return (
    <Flex
      width={horizontal ? '100%' : '1px'}
      backgroundColor='#000'
      height={horizontal ? '1px' : '100%'}
      position='absolute'
      left={horizontal ? 0 : '50%'}
      top={horizontal ? '50%' : 0}
      transform={horizontal ? 'translateY(-50%)' : 'translateX(-50%)'}
    />
  );
};

const VisualFieldMatrix = ({ testInProgress, testProgressData, pointIsBeingDisplayed, dotMatrixFromResults }) => {
  const [rawDotDataset, setRawDotDataset] = useState([]);
  const [usingOldData, setUsingOldData] = useState(false);
  const [allDots, setAllDots] = useState([]);
  const [outOfBoundariesDots, setOutOfBoundariesDots] = useState([]);
  const [lineLength, setLineLength] = useState(0);
  const { missedOnceDots, missedTwiceDots, orientationIncorrect, orientationCorrect, activeDot } = testProgressData || {};
  const { sm } = useBreakpoints();
  const {
    testData: { forthValleyVisualFieldDots },
  } = useTestContext();

  useEffect(() => {
    const getLineLength = () => {
      const maxValue = 325;
      const responsiveValue = sm && !dotMatrixFromResults ? 205 : window.innerWidth / (dotMatrixFromResults ? 4.5 : 4);
      //re-examine & simplify this logic.
      return setLineLength(Math.min(responsiveValue, maxValue));
    };

    getLineLength();

    window.addEventListener('resize', getLineLength);

    return () => {
      window.removeEventListener('resize', getLineLength);
    };
  }, [sm, dotMatrixFromResults]);

  useEffect(() => {
    //handle post-test or during-test matrices
    if (dotMatrixFromResults) {
      setRawDotDataset(dotMatrixFromResults);
    } else setRawDotDataset(forthValleyVisualFieldDots);
  }, [dotMatrixFromResults, forthValleyVisualFieldDots]);

  useEffect(() => {
    if (rawDotDataset?.some((dot) => dot.id.includes('stimulus'))) setUsingOldData(true);
  }, [rawDotDataset]);

  const getDotColor = (dot) => {
    switch (true) {
      case missedOnceDots?.some((missedDot) => missedDot.id === dot.id):
        return '#F0B729';
      case missedTwiceDots?.some((missedDot) => missedDot.id === dot.id):
        return 'red';
      case orientationCorrect?.some((seenDot) => seenDot.id === dot.id):
        return '#6DC89D';
      case orientationIncorrect?.some((seenDot) => seenDot.id === dot.id):
        return '#870000';
      case outOfBoundariesDots?.some((outOfBoundsDot) => outOfBoundsDot.id === dot.id):
        return '#F2E6E6';
      case dot.id === 'coord15-1.5' || dot.id === 'coord-15-1.5':
        //blindspot
        return '#000';
      default:
        return '#AAB2B2';
    }
  };

  useEffect(() => {
    const getActualPointOnGraph = (degreeX, degreeY) => {
      const getMaxValue = () => {
        const allCoords = rawDotDataset.flatMap((coordObj) => {
          return Object.values(coordObj).filter((coord) => {
            return Math.abs(coord);
          });
        });
        return Math.max(...allCoords);
      };
      const maximumDegree = getMaxValue();
      const halfLineLength = lineLength / 2;
      const sections = halfLineLength / maximumDegree;
      let x = Math.floor(Math.abs((maximumDegree + degreeX) * sections));
      let y = Math.floor(Math.abs((maximumDegree + degreeY) * sections));

      [x, y] = [x, y].map((coord) => {
        //add padding to extreme coordinates
        if (coord === 0) return (coord += lineLength * 0.024);
        if (coord === lineLength) return (coord -= lineLength * 0.024);
        return coord;
      });

      return { x, y };
    };

    const renderCoords = () => {
      const parsedDots = rawDotDataset?.map((coordSet) => {
        const legacyId = `stimulus-point-coordinate_${coordSet.degreeX}-${coordSet.degreeY}`;
        const newMatrixId = `coord${coordSet.degreeX}${coordSet.degreeY}`;
        const invertedY = coordSet.degreeY * -1;
        //invert because CSS Y positions & actual graph Y positions are opposite
        const { x, y } = getActualPointOnGraph(coordSet.degreeX, invertedY);
        if (coordSet.outOfBoundaries)
          setOutOfBoundariesDots((existingDots) => {
            return [...existingDots, coordSet];
          });
        return { id: usingOldData ? legacyId : newMatrixId, left: x, top: y };
      });

      return parsedDots;
    };
    setAllDots(renderCoords());
  }, [rawDotDataset, lineLength, usingOldData]);

  return (
    <Flex
      height={`${lineLength + lineLength * 0.05}px`}
      width={`${lineLength + lineLength * 0.05}px`}
      //this is a container so slightly bigger than the actual matrix
      border={`3px solid ${testInProgress ? theme.colors.primary : 'grey'}`}
      borderRadius='12px'
      centered
    >
      <Flex centered height={`${lineLength}px`} width={`${lineLength}px`} position='relative'>
        <GraphLine horizontal />
        <GraphLine vertical />
        {allDots?.map((dot) => {
          return (
            <Flex
              key={dot.id}
              id={dot.id}
              height={`${lineLength * 0.05}px`}
              width={`${lineLength * 0.05}px`}
              borderRadius='50%'
              position='absolute'
              transform='translate(-50%, -50%)'
              animation={activeDot?.id === dot.id && pointIsBeingDisplayed ? `${blink} 750ms infinite` : null}
              backgroundColor={getDotColor(dot)}
              style={{
                top: dot.top,
                left: dot.left,
              }}
            />
          );
        })}
      </Flex>
    </Flex>
  );
};

export default VisualFieldMatrix;
